import { useState } from "react";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "../../common/notice-banner.scss";

export interface IAiModelDeprecationBannersProps {
  shutdownItems: string[];
  deprecatedItems: string[];
  onShutdownAction?: () => void;
  onDeprecatedAction?: () => void;
  shutdownActionLabel?: string;
  deprecatedActionLabel?: string;
  className?: string;
  /** "editor" hides action buttons and shows in-editor guidance text */
  variant?: "hub" | "editor";
}

export default function AiModelDeprecationBanners({
  shutdownItems,
  deprecatedItems,
  onShutdownAction,
  onDeprecatedAction,
  shutdownActionLabel = "FIX NOW",
  deprecatedActionLabel = "REVIEW ALL",
  className,
  variant = "hub"
}: IAiModelDeprecationBannersProps) {
  const isEditor = variant === "editor";
  const [closedBanners, setClosedBanners] = useState<Set<"shutdown" | "deprecated">>(new Set());

  if (shutdownItems.length === 0 && deprecatedItems.length === 0) {
    return null;
  }

  const closeBanner = (id: "shutdown" | "deprecated") => {
    setClosedBanners((prev) => new Set([...prev, id]));
  };

  return (
    <div className={`kuika-notice-banner-stack${className ? ` ${className}` : ""}`}>
      {shutdownItems.length > 0 && !closedBanners.has("shutdown") && (
        <div className="kuika-notice-banner kuika-notice-banner--danger">
          <div className="kuika-notice-banner__body">
            <div className="kuika-notice-banner__header">
              <span className="kuika-notice-banner__badge kuika-notice-banner__badge--danger">
                <KuikaSvg type="common" name="ban" width={12} height={12} color="var(--dark-0)" />
                SHUTDOWN
              </span>
              <span className="kuika-notice-banner__title kuika-notice-banner__title--danger">
                {isEditor
                  ? "This model is no longer available — select a replacement to continue"
                  : `${shutdownItems.length} ${shutdownItems.length === 1 ? "module is" : "modules are"} using a shutdown model - immediate action required`}
              </span>
            </div>
            <div className="kuika-notice-banner__description kuika-notice-banner__description--danger">
              {isEditor ? (
                <>
                  <strong>{shutdownItems.join(", ")}</strong> {shutdownItems.length === 1 ? "has" : "have"} been shut
                  down. This module cannot run until you choose a new model from the dropdown above.
                </>
              ) : (
                <>
                  <strong>{shutdownItems.join(", ")} </strong>
                  {shutdownItems.length === 1 ? "uses " : "use "}
                  models that have been shut down. This module is currently non-functional.
                </>
              )}
            </div>
          </div>
          <div className="kuika-notice-banner__actions">
            {!isEditor && (
              <button className="kuika-notice-banner__btn-action" onClick={onShutdownAction}>
                {shutdownActionLabel}
              </button>
            )}
            <button className="kuika-notice-banner__btn-close" onClick={() => closeBanner("shutdown")} title="Close">
              <KuikaSvg type="common" name="x" width={16} height={16} color="var(--dark-6)" />
            </button>
          </div>
        </div>
      )}
      {deprecatedItems.length > 0 && !closedBanners.has("deprecated") && (
        <div className="kuika-notice-banner kuika-notice-banner--warning">
          <div className="kuika-notice-banner__body">
            <div className="kuika-notice-banner__header">
              <span className="kuika-notice-banner__badge kuika-notice-banner__badge--warning">
                <KuikaSvg type="common" name="alert-triangle" width={12} height={12} color="var(--dark-0)" />
                DEPRECATED
              </span>
              <span className="kuika-notice-banner__title kuika-notice-banner__title--warning">
                {isEditor
                  ? "This model is deprecated — consider switching to a newer version"
                  : `${deprecatedItems.length} ${deprecatedItems.length === 1 ? "module is" : "modules are"} using deprecated models — update recommended`}
              </span>
            </div>
            <div className="kuika-notice-banner__description kuika-notice-banner__description--warning">
              {isEditor ? (
                <>
                  <strong>{deprecatedItems.join(", ")}</strong> {deprecatedItems.length === 1 ? "has" : "have"} been
                  deprecated. Update to a newer model version to avoid future disruption.
                </>
              ) : (
                <>
                  <strong>{deprecatedItems.join(", ")} </strong>
                  {deprecatedItems.length === 1 ? "uses" : "use"} models that have been deprecated. Update to a newer
                  model version.
                </>
              )}
            </div>
          </div>
          <div className="kuika-notice-banner__actions">
            {!isEditor && (
              <button className="kuika-notice-banner__btn-action" onClick={onDeprecatedAction}>
                {deprecatedActionLabel}
              </button>
            )}
            <button className="kuika-notice-banner__btn-close" onClick={() => closeBanner("deprecated")} title="Close">
              <KuikaSvg type="common" name="x" width={16} height={16} color="var(--dark-6)" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
